import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { useGlobalPassAutoReturn } from "./utils/globalPass";
import { ChatProvider } from "./contexts/ChatContext";
import { CategoryProvider } from "./contexts/CategoryContext";
import { SubCategoryProvider } from "./contexts/SubCategoryContext";
import { HeadlineProvider } from "./contexts/HeadlineContext";

import Login from "./components/auth/Login";
import Register from "./components/auth/Register";
import AdminPanel from "./components/admin/AdminPanel";
import CategoryManager from "./components/chat/CategoryManager";
import VocabularyManager from "./components/admin/VocabularyManager";
import UserManager from "./components/chat/UserManager";
import SubscriptionPlanManager from "./components/admin/SubscriptionPlanManager";
import Settings from "./components/chat/Settings";

import Sidebar from "./components/chat/Sidebar";
import { applyTheme, useTheme } from "./theme/useTheme";
import Footer from "./components/chat/Footer";
import SiteFooter from "./components/common/SiteFooter";
import ChatInterface from "./components/chat/ChatInterface";
import Sanctuary from "./components/chat/Sanctuary";
import TesterHome from "./components/chat/TesterHome";
import Journey from "./components/chat/Journey";
import Subscription from "./components/subscription/Subscription";
import PaymentSuccess from "./components/subscription/PaymentSuccess";
import PaymentCancel from "./components/subscription/PaymentCancel";
import Terms from "./components/legal/Terms";
import Privacy from "./components/legal/Privacy";
import RefundPolicy from "./components/legal/RefundPolicy";
import EnergyMatchDesign from "./components/astro/EnergyMatchDesign";
import MarriageVerdictForm from "./components/astro/MarriageVerdictForm";
import MarriageVerdictResult from "./components/astro/MarriageVerdictResult";
import CheckMateForm from "./components/astro/CheckMateForm";
import CheckMateResult from "./components/astro/CheckMateResult";
import MatescanForm from "./components/astro/MatescanForm";
import MatescanResult from "./components/astro/MatescanResult";
import EnergyMatchV3Form from "./components/astro/EnergyMatchV3Form";
import EnergyMatchV3Result from "./components/astro/EnergyMatchV3Result";
import SajuForm from "./components/astro/SajuForm";
import SajuResult from "./components/astro/SajuResult";
import JPTarotPage from "./components/astro/JPTarotPage";
import KyuseiJapanForm from "./components/astro/KyuseiJapanForm";
import KyuseiJapanResult from "./components/astro/KyuseiJapanResult";
import JPTimingFlowResult from "./components/astro/JPTimingFlowResult";
import JPCompanionForm from "./components/astro/JPCompanionForm";
import JPCompanionResult from "./components/astro/JPCompanionResult";
import GCCV2DobForm from "./components/astro/gccV2/GCCV2DobForm";
import GCCV2TimingResult from "./components/astro/gccV2/GCCV2TimingResult";
import GCCV2KyuseiResult from "./components/astro/gccV2/GCCV2KyuseiResult";
import GCCV2CompanionResult from "./components/astro/gccV2/GCCV2CompanionResult";
import GCCV2EmotionalIntelligenceResult from "./components/astro/gccV2/GCCV2EmotionalIntelligenceResult";
import GCCV2PersonalityEngineResult from "./components/astro/gccV2/GCCV2PersonalityEngineResult";
import SambandhTaalMelTab from "./components/astro/indiaV2/tabs/SambandhTaalMelTab";
import VivahMuhuratTab from "./components/astro/indiaV2/tabs/VivahMuhuratTab";
import UpayMargTab from "./components/astro/indiaV2/tabs/UpayMargTab";
import BhavnaDrishtiTab from "./components/astro/indiaV2/tabs/BhavnaDrishtiTab";
import VyaktitvaDarshanTab from "./components/astro/indiaV2/tabs/VyaktitvaDarshanTab";
import SamayPravahTab from "./components/astro/indiaV2/tabs/SamayPravahTab";
import AapkaNoteTab from "./components/astro/indiaV2/tabs/AapkaNoteTab";
import EnergyMatchTab from "./components/astro/UkV2/tabs/EnergyMatchTab";
import MateScanTab from "./components/astro/UkV2/tabs/MateScanTab";
import RelationshipTab from "./components/astro/UkV2/tabs/RelationshipTab";
import UKCompatibilityTab from "./components/astro/UkV2/tabs/CompatibilityTab";
import CompanionTalkTab from "./components/astro/UkV2/tabs/CompanionTalkTab";
import CosmicUKTab from "./components/astro/UkV2/tabs/CosmicUKTab";
import DailyFlowTab from "./components/astro/UkV2/tabs/DailyFlowTab";
import EmotionalRhythmTab from "./components/astro/UkV2/tabs/EmotionalRhythmTab";
import ZodiacPersonalityTab from "./components/astro/UkV2/tabs/ZodiacPersonalityTab";
import ZodiacHomeScreen from "./components/astro/UkV2/ZodiacHomeScreen";
import PhilippinesV2Tab from "./components/astro/philippinesV2/PhilippinesV2Tab";
import IndonesiaV2Tab from "./components/astro/indonesiaV2/IndonesiaV2Tab";
import VietnamV2Tab from "./components/astro/vietnamV2/VietnamV2Tab";
import BirthChartTab from "./components/astro/astriaVietnam/tabs/BirthChartTab";
import LuckyDayTab from "./components/astro/astriaVietnam/tabs/LuckyDayTab";
import CompatibilityTab from "./components/astro/astriaVietnam/tabs/CompatibilityTab";
import SpaceEnergyTab from "./components/astro/astriaVietnam/tabs/SpaceEnergyTab";
import TarotReadingTab from "./components/astro/astriaVietnam/tabs/TarotReadingTab";
import BrazilV2Tab from "./components/astro/brazilV2/BrazilV2Tab";
import MexicoV2Tab from "./components/astro/mexicoV2/MexicoV2Tab";
import MexicoHomeTab from "./components/astro/Mexico/HomeTab";
import MexicoEnergyMatchTab from "./components/astro/Mexico/tabs/EnergyMatchTab";
import MexicoCompatibilityTab from "./components/astro/Mexico/tabs/CompatibilityTab";
import MexicoCosmicMessageTab from "./components/astro/Mexico/tabs/CosmicMessageTab";
import MexicoRelationshipTab from "./components/astro/Mexico/tabs/RelationshipTab";
import MexicoCompanionTalkTab from "./components/astro/Mexico/tabs/CompanionTalkTab";
import MexicoDailyFlowTab from "./components/astro/Mexico/tabs/DailyFlowTab";
import MexicoZodiacPersonalityTab from "./components/astro/Mexico/tabs/ZodiacPersonalityTab";
import { CompatibilityForm } from "./components/socialCompatability/CompatibilityForm";
import { MatchesList } from "./components/socialCompatability/MatchesList";
import { RequestHistory } from "./components/socialCompatability/RequestHistory";
import { SynastryReportScreen } from "./components/socialCompatability/SynastryReportScreen";
import { CompatibilityChatScreen } from "./components/socialCompatability/CompatibilityChatScreen";

import AppleCallback from "./components/auth/AppleCallback";
import AppleSuccess from "./components/AppleSuccess";

import "./App.css";
import TarotCardCategory from "./components/Tarot/TarotCardCategory";
// import UranianAstrologyTab from "./components/Uranian/Uranian";
import LifeGraph from "./components/LifeGraph/LifeGraph";
import LifeGPS from "./components/LifeGPS/LifeGPS";
import LetterMode from "./components/astria/LetterMode";
import TodayPage from "./components/astria/TodayPage";
import AstrologyPage from "./components/astria/AstrologyPage";
import JourneyStoryPage from "./components/astria/JourneyStoryPage";
import AccountPage from "./components/astria/AccountPage";
import { LockPreview, TabGate } from "./components/astria/LockCard";
import FreeEntryGate from "./components/astria/FreeEntryGate";
import EnergyMatchV2 from "./components/astro/EnergyMatchV2";
import IndexBuilderPage from "./components/Index/IndexBuilderPage";
import ErrorLogsPage from "./components/common/ErrorLogsPage";
import { clearAstriaGCCV2ResponseCache } from "./utils/GCC/astriaGCCV2ResponseCache";
import { clearStoredAstriaGCCV2Dob } from "./utils/GCC/astriaGCCV2Dob";
import IndonesiaModules from "./components/astro/indonesiaV2/IndonesiaModules";

// Marketing page is only needed by signed-out visitors; keep it out of the app bundle.
const AstriaLanding = lazy(() => import("./components/landing/AstriaLanding"));
// import HomeScreen from "./components/astro/UkV2/HomeScreen";

/* ---------- Protected Route ---------- */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

/* ---------- Domain Gate: on the test domain, only testers (roleId 3) can log in; others are redirected to /login ---------- */
const TESTER_ROLE_ID = 3;

const GCC_V2_PATH_PREFIX = "/astria-gcc-v2";

const DomainGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading, logout } = useAuth();
  useGlobalPassAutoReturn(); // pass ended → back to the home country, on any page
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    document.title = "Astria";
    const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (icon) icon.href = "/astria_logo.png";
  }, [user?.region]);

  // GCC v2's per-tab AI response cache (sessionStorage) and stored DOB must
  const wasInGCCV2Ref = useRef(
    location.pathname.startsWith(GCC_V2_PATH_PREFIX),
  );
  useEffect(() => {
    const isInGCCV2 = location.pathname.startsWith(GCC_V2_PATH_PREFIX);
    if (wasInGCCV2Ref.current && !isInGCCV2) {
      clearAstriaGCCV2ResponseCache();
      clearStoredAstriaGCCV2Dob();
    }
    wasInGCCV2Ref.current = isInGCCV2;
  }, [location.pathname]);

  return <>{children}</>;
};

/* ---------- Chat Layout Inline ---------- */
const TESTER_ROLE_ID_CHATPAGE = 3;

const ChatPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const isTester = user?.roleId === TESTER_ROLE_ID_CHATPAGE;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // const lastScrollTop = useRef(0);
  const [showFooter, setShowFooter] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isJapanCompatActive, setIsJapanCompatActive] = useState(false);

  let PageContent;
  // These carry a :requestId param, so they're checked before the exact-string switch below.
  if (location.pathname.startsWith("/compatibility-synastry/")) {
    PageContent = <SynastryReportScreen />;
  } else if (location.pathname.startsWith("/compatibility-chat/")) {
    PageContent = <CompatibilityChatScreen />;
  } else
    switch (location.pathname) {
      case "/sanctuary":
        PageContent = (
          <ChatInterface
            showFooter={false}
            onJapanCompatActive={setIsJapanCompatActive}
          />
        );
        break;
      case "/indomodule":
        PageContent = <IndonesiaModules />;
        break;
      // case "/Ukhome":
      //   PageContent = <HomeScreen />;
      //   break;
      case "/journey":
        PageContent = <Journey />;
        break;
      case "/":
        // Logged-in home is Today; the region home (Sanctuary) moved to /home.
        PageContent =
          isAuthenticated && !isTester ? (
            <TodayPage />
          ) : isTester ? (
            <TesterHome />
          ) : (
            <Sanctuary />
          );
        break;
      case "/home":
        PageContent = <Sanctuary />;
        break;
      case "/letter":
        PageContent = (
          <div className="astria-themable relative flex flex-col h-full">
            <LetterMode onExit={() => navigate("/today")} />
          </div>
        );
        break;
      case "/today":
        PageContent = <TodayPage />;
        break;
      case "/astrology":
        PageContent = (
          <TabGate path="/astrology">
            <AstrologyPage />
          </TabGate>
        );
        break;
      case "/lock":
        PageContent = <LockPreview />;
        break;
      case "/journey-story":
        PageContent = <JourneyStoryPage />;
        break;
      case "/account/profile":
      case "/account/language":
      case "/account/atmosphere":
      case "/account/memory-privacy":
      case "/account/plans-billing":
        PageContent = <AccountPage />;
        break;
      case "/tarotCard":
        PageContent = <TarotCardCategory />;
        break;
      // case "/uranian":
      //   PageContent = <UranianAstrologyTab />;
      //   break;
      case "/lifeGraph":
        PageContent = <LifeGraph />;
        break;
      case "/lifeGps":
        PageContent = <LifeGPS />;
        break;
      case "/sentence":
        PageContent = <IndexBuilderPage />;
        break;
      case "/energyMatch":
        PageContent = <EnergyMatchDesign />;
        break;
      case "/energyMatchV3":
        PageContent = <EnergyMatchV3Form />;
        break;
      case "/energyMatchV3/result":
        PageContent = <EnergyMatchV3Result />;
        break;
      case "/energyMatchV2":
        PageContent = <EnergyMatchV2 />;
        break;
      case "/marriage-verdict":
        PageContent = <MarriageVerdictForm />;
        break;
      case "/marriage-verdict/result":
        PageContent = <MarriageVerdictResult />;
        break;
      case "/checkmate":
        PageContent = <CheckMateForm />;
        break;
      case "/checkmate/result":
        PageContent = <CheckMateResult />;
        break;
      case "/matescan":
        PageContent = <MatescanForm />;
        break;
      case "/matescan/result":
        PageContent = <MatescanResult />;
        break;
      case "/saju":
        PageContent = <SajuForm />;
        break;
      case "/saju/result":
        PageContent = <SajuResult />;
        break;
      case "/jp-tarot":
        PageContent = <JPTarotPage />;
        break;
      case "/astria-japan/kyusei":
        PageContent = <KyuseiJapanForm mode="daily" />;
        break;
      case "/astria-japan/kyusei/result":
        PageContent = <KyuseiJapanResult />;
        break;
      case "/astria-japan/timing":
        PageContent = <KyuseiJapanForm mode="timing" />;
        break;
      case "/astria-japan/timing/result":
        PageContent = <JPTimingFlowResult />;
        break;
      case "/astria-japan/companion":
        PageContent = <JPCompanionForm />;
        break;
      case "/astria-japan/companion/result":
        PageContent = <JPCompanionResult />;
        break;
      case "/astria-gcc-v2/dob":
        PageContent = <GCCV2DobForm />;
        break;
      case "/astria-gcc-v2/timing":
        PageContent = <GCCV2TimingResult />;
        break;
      case "/astria-gcc-v2/kyusei":
        PageContent = <GCCV2KyuseiResult />;
        break;
      case "/astria-gcc-v2/companion":
        PageContent = <GCCV2CompanionResult />;
        break;
      case "/astria-gcc-v2/emotional-intelligence":
        PageContent = <GCCV2EmotionalIntelligenceResult />;
        break;
      case "/astria-gcc-v2/personality-engine":
        PageContent = <GCCV2PersonalityEngineResult />;
        break;
      case "/astria-india-v2/sambandh":
        PageContent = <SambandhTaalMelTab />;
        break;
      case "/astria-india-v2/vivah":
        PageContent = <VivahMuhuratTab />;
        break;
      case "/astria-india-v2/upay":
        PageContent = <UpayMargTab />;
        break;
      case "/astria-india-v2/bhavna":
        PageContent = <BhavnaDrishtiTab />;
        break;
      case "/astria-india-v2/vyaktitva":
        PageContent = <VyaktitvaDarshanTab />;
        break;
      case "/astria-india-v2/samay":
        PageContent = <SamayPravahTab />;
        break;
      case "/astria-india-v2/aapka-note":
        PageContent = <AapkaNoteTab />;
        break;
      case "/astria-uk-v2/energy-match":
        PageContent = <EnergyMatchTab />;
        break;
      case "/astria-uk-v2/matescan":
        PageContent = <MateScanTab />;
        break;
      case "/astria-uk-v2/relationship":
        PageContent = <RelationshipTab />;
        break;
      case "/astria-uk-v2/compatibility":
        PageContent = <UKCompatibilityTab />;
        break;
      case "/astria-uk-v2/companion-talk":
        PageContent = <CompanionTalkTab />;
        break;
      case "/astria-uk-v2/cosmic-uk":
        PageContent = <CosmicUKTab />;
        break;
      case "/astria-uk-v2/daily-flow":
        PageContent = <DailyFlowTab />;
        break;
      case "/astria-uk-v2/emotional-rhythm":
        PageContent = <EmotionalRhythmTab />;
        break;
      case "/astria-uk-v2/zodiac-personality":
        PageContent = <ZodiacPersonalityTab />;
        break;
      case "/astria-uk-v2/zodiac-home":
        PageContent = <ZodiacHomeScreen />;
        break;
      case "/astria-mexico/home":
        PageContent = <MexicoHomeTab />;
        break;
      case "/astria-mexico/energy-match":
        PageContent = <MexicoEnergyMatchTab />;
        break;
      case "/astria-mexico/compatibility":
        PageContent = <MexicoCompatibilityTab />;
        break;
      case "/astria-mexico/cosmic-message":
        PageContent = <MexicoCosmicMessageTab />;
        break;
      case "/astria-mexico/relationship":
        PageContent = <MexicoRelationshipTab />;
        break;
      case "/astria-mexico/companion-talk":
        PageContent = <MexicoCompanionTalkTab />;
        break;
      case "/astria-mexico/daily-flow":
        PageContent = <MexicoDailyFlowTab />;
        break;
      case "/astria-mexico/zodiac-personality":
        PageContent = <MexicoZodiacPersonalityTab />;
        break;
      // key={...} forces remount per tab so local state doesn't leak between tabs.
      case "/astria-philippines-v2/soft-summary":
        PageContent = (
          <PhilippinesV2Tab key="soft_summary" tabKey="soft_summary" />
        );
        break;
      case "/astria-philippines-v2/connection-atmosphere":
        PageContent = (
          <PhilippinesV2Tab
            key="connection_atmosphere"
            tabKey="connection_atmosphere"
          />
        );
        break;
      case "/astria-philippines-v2/timing-rhythm":
        PageContent = (
          <PhilippinesV2Tab key="timing_rhythm" tabKey="timing_rhythm" />
        );
        break;
      case "/astria-philippines-v2/emotion-balance":
        PageContent = (
          <PhilippinesV2Tab key="emotion_balance" tabKey="emotion_balance" />
        );
        break;
      case "/astria-philippines-v2/guidance-path":
        PageContent = (
          <PhilippinesV2Tab key="guidance_path" tabKey="guidance_path" />
        );
        break;
      case "/astria-philippines-v2/your-note":
        PageContent = <PhilippinesV2Tab key="your_note" tabKey="your_note" />;
        break;
      case "/astria-indonesia-v2/soft-summary":
        PageContent = (
          <IndonesiaV2Tab key="soft_summary" tabKey="soft_summary" />
        );
        break;
      case "/astria-indonesia-v2/connection-atmosphere":
        PageContent = (
          <IndonesiaV2Tab
            key="connection_atmosphere"
            tabKey="connection_atmosphere"
          />
        );
        break;
      case "/astria-indonesia-v2/timing-rhythm":
        PageContent = (
          <IndonesiaV2Tab key="timing_rhythm" tabKey="timing_rhythm" />
        );
        break;
      case "/astria-indonesia-v2/emotion-balance":
        PageContent = (
          <IndonesiaV2Tab key="emotion_balance" tabKey="emotion_balance" />
        );
        break;
      case "/astria-indonesia-v2/guidance-path":
        PageContent = (
          <IndonesiaV2Tab key="guidance_path" tabKey="guidance_path" />
        );
        break;
      case "/astria-indonesia-v2/your-note":
        PageContent = <IndonesiaV2Tab key="your_note" tabKey="your_note" />;
        break;
      case "/astria-vietnam-v2/soft-summary":
        PageContent = <VietnamV2Tab key="soft_summary" tabKey="soft_summary" />;
        break;
      case "/astria-vietnam-v2/connection-atmosphere":
        PageContent = (
          <VietnamV2Tab
            key="connection_atmosphere"
            tabKey="connection_atmosphere"
          />
        );
        break;
      case "/astria-vietnam-v2/timing-rhythm":
        PageContent = (
          <VietnamV2Tab key="timing_rhythm" tabKey="timing_rhythm" />
        );
        break;
      case "/astria-vietnam-v2/emotion-balance":
        PageContent = (
          <VietnamV2Tab key="emotion_balance" tabKey="emotion_balance" />
        );
        break;
      case "/astria-vietnam-v2/guidance-path":
        PageContent = (
          <VietnamV2Tab key="guidance_path" tabKey="guidance_path" />
        );
        break;
      case "/astria-vietnam-v2/your-note":
        PageContent = <VietnamV2Tab key="your_note" tabKey="your_note" />;
        break;
      case "/astria-vietnam/birth-chart":
        PageContent = <BirthChartTab />;
        break;
      case "/astria-vietnam/lucky-day":
        PageContent = <LuckyDayTab />;
        break;
      case "/astria-vietnam/compatibility":
        PageContent = <CompatibilityTab />;
        break;
      case "/astria-vietnam/space-energy":
        PageContent = <SpaceEnergyTab />;
        break;
      case "/astria-vietnam/tarot":
        PageContent = <TarotReadingTab />;
        break;
      case "/astria-brazil-v2/soft-summary":
        PageContent = <BrazilV2Tab key="soft_summary" tabKey="soft_summary" />;
        break;
      case "/astria-brazil-v2/connection-atmosphere":
        PageContent = (
          <BrazilV2Tab
            key="connection_atmosphere"
            tabKey="connection_atmosphere"
          />
        );
        break;
      case "/astria-brazil-v2/timing-rhythm":
        PageContent = (
          <BrazilV2Tab key="timing_rhythm" tabKey="timing_rhythm" />
        );
        break;
      case "/astria-brazil-v2/emotion-balance":
        PageContent = (
          <BrazilV2Tab key="emotion_balance" tabKey="emotion_balance" />
        );
        break;
      case "/astria-brazil-v2/guidance-path":
        PageContent = (
          <BrazilV2Tab key="guidance_path" tabKey="guidance_path" />
        );
        break;
      case "/astria-brazil-v2/your-note":
        PageContent = <BrazilV2Tab key="your_note" tabKey="your_note" />;
        break;
      case "/astria-mexico-v2/soft-summary":
        PageContent = <MexicoV2Tab key="soft_summary" tabKey="soft_summary" />;
        break;
      case "/astria-mexico-v2/connection-atmosphere":
        PageContent = (
          <MexicoV2Tab
            key="connection_atmosphere"
            tabKey="connection_atmosphere"
          />
        );
        break;
      case "/astria-mexico-v2/timing-rhythm":
        PageContent = (
          <MexicoV2Tab key="timing_rhythm" tabKey="timing_rhythm" />
        );
        break;
      case "/astria-mexico-v2/emotion-balance":
        PageContent = (
          <MexicoV2Tab key="emotion_balance" tabKey="emotion_balance" />
        );
        break;
      case "/astria-mexico-v2/guidance-path":
        PageContent = (
          <MexicoV2Tab key="guidance_path" tabKey="guidance_path" />
        );
        break;
      case "/astria-mexico-v2/your-note":
        PageContent = <MexicoV2Tab key="your_note" tabKey="your_note" />;
        break;
      case "/compatibility-form":
        PageContent = <CompatibilityForm />;
        break;
      case "/matches":
        PageContent = <MatchesList />;
        break;
      case "/history":
        PageContent = <RequestHistory />;
        break;
      // case "/subscription":
      //   PageContent = <Subscription />;
      //   break;
      default:
        PageContent = isTester ? <TesterHome /> : <Sanctuary />;
    }

  // Listen for sidebar state changes
  useEffect(() => {
    const handleSidebarStateChange = (event: CustomEvent) => {
      setIsMobileSidebarOpen(event.detail.isOpen);
    };

    window.addEventListener(
      "sidebarStateChange",
      handleSidebarStateChange as EventListener,
    );

    return () => {
      window.removeEventListener(
        "sidebarStateChange",
        handleSidebarStateChange as EventListener,
      );
    };
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const isMobile = window.innerWidth < 768;

    const onScroll = () => {
      if (isMobile) {
        if (isMobileSidebarOpen) {
          setShowFooter(!isMobileSidebarOpen);
          return;
        }
        const threshold = 10;
        const atBottom =
          el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

        setShowFooter(atBottom);
        return;
      }

      const threshold = 10;
      const atBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;

      setShowFooter(atBottom);
    };

    onScroll();

    el.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [isMobileSidebarOpen]);

  const { theme } = useTheme();
  const effectiveTheme = theme;
  applyTheme(effectiveTheme); // synchronous, so the page renders with the right palette

  // Signed-out visitors on the Astria domain get the full-screen marketing page
  // instead of the app shell. `isAuthenticated` is seeded synchronously from the
  // stored token, so signed-in users never see this flash by on reload.
  if (!isAuthenticated && location.pathname === "/") {
    return (
      <Suspense fallback={<div className="fixed inset-0" style={{ background: "#0F0A14" }} />}>
        <AstriaLanding />
      </Suspense>
    );
  }

  const hasFooterPad =
    isAuthenticated &&
    location.pathname !== "/account/plans-billing" &&
    location.pathname !== "/letter" &&
    location.pathname !== "/sanctuary" &&
    location.pathname !== "/lifeGps" &&
    !location.pathname.startsWith("/saju");

  return (
    <div className="flex h-screen h-[100dvh] overflow-hidden bg-astria-bg">
      <FreeEntryGate />
      <Sidebar />

      <div
        className="astria-themable relative flex-1 overflow-hidden"
        style={location.pathname === "/letter" ? { background: "var(--astria-surface-soft)" } : undefined}
      >
        <div
          ref={scrollRef}
          style={
            location.pathname.startsWith("/saju")
              ? { background: "#EFE3D3", WebkitMaskImage: "none", maskImage: "none" }
              : undefined
          }
          className={`astria-scroll relative z-10 h-full overflow-y-auto ${hasFooterPad ? "pb-24 astria-scroll-padded" : ""}`}
        >
          <div
            key={location.pathname}
            className={`astria-view ${location.pathname === "/letter" ? "h-full" : ""}`}
          >
            {PageContent}
          </div>
          {!isAuthenticated && <SiteFooter />}
        </div>

        <div
          className={`absolute bottom-3 left-1/2 -translate-x-1/2 z-50 transition-all duration-300 ${
            showFooter
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-10 pointer-events-none"
          } ${isAuthenticated ? "" : "hidden"}`}
        >
          <div id="globalFooter" className="h-[60px]">
            {isTester ||
            location.pathname === "/sanctuary" ||
            location.pathname === "/letter" ||
            location.pathname === "/account/plans-billing" ||
            location.pathname === "/tarotCard" ||
            location.pathname === "/lifeGps" ||
            location.pathname === "/energyMatch" ||
            location.pathname.startsWith("/energyMatchV3") ||
            location.pathname === "/energyInsights" ||
            location.pathname === "/energyChat" ||
            location.pathname === "/energyProfile" ||
            location.pathname.startsWith("/marriage-verdict") ||
            location.pathname.startsWith("/checkmate") ||
            location.pathname.startsWith("/matescan") ||
            location.pathname.startsWith("/saju") ||
            location.pathname === "/jp-tarot" ||
            location.pathname.startsWith("/astria-japan/kyusei") ||
            location.pathname.startsWith("/astria-japan/timing") ||
            location.pathname.startsWith("/astria-japan/companion") ||
            location.pathname.startsWith("/astria-gcc-v2") ||
            location.pathname.startsWith("/astria-india-v2") ||
            location.pathname.startsWith("/astria-philippines-v2") ||
            location.pathname.startsWith("/astria-indonesia-v2") ||
            location.pathname.startsWith("/astria-vietnam-v2") ||
            location.pathname.startsWith("/astria-vietnam") ||
            location.pathname.startsWith("/astria-brazil-v2") ||
            location.pathname.startsWith("/astria-mexico-v2") ||
            location.pathname.startsWith("/astria-mexico") ||
            location.pathname.startsWith("/astria-uk-v2") ||
            location.pathname === "/compatibility-form" ||
            location.pathname.startsWith("/compatibility-chat/") ||
            location.pathname.startsWith("/compatibility-synastry/") ||
            isJapanCompatActive ? (
              ""
            ) : (
              <Footer />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* Standalone routes render outside the app shell, so they carry the light-theme scope themselves. */
const Themed: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="astria-themable" style={{ display: "contents" }}>
    {children}
  </div>
);

/* ---------- App ---------- */
function App() {
  return (
    <Router>
      <AuthProvider>
        <HeadlineProvider>
          <CategoryProvider>
            <SubCategoryProvider>
              <ChatProvider>
                <Toaster
                  position="top-right"
                  toastOptions={{
                    duration: 3000,
                    style: { background: "#44304e", color: "#fff" },
                  }}
                />

                <DomainGate>
                  <Routes>
                    {/* Auth */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/error" element={<ErrorLogsPage />} />

                    <Route path="/indomodule" element={<IndonesiaModules />} />
                    {/* <Route path="/Ukhome" element={<HomeScreen />} /> */}

                    {/* All chat pages use same layout */}
                    <Route path="/" element={<ChatPage />} />
                    <Route path="/sanctuary" element={<ChatPage />} />
                    <Route path="/journey" element={<ChatPage />} />
                    <Route path="/today" element={<ChatPage />} />
                    <Route path="/letter" element={<ChatPage />} />
                    <Route path="/home" element={<ChatPage />} />
                    <Route path="/astrology" element={<ChatPage />} />
                    <Route path="/lock" element={<ChatPage />} />
                    <Route path="/journey-story" element={<ChatPage />} />
                    <Route path="/account/:section" element={<ChatPage />} />
                    <Route path="/plans-billing" element={<Navigate to="/account/plans-billing" replace />} />
                    <Route path="/sentence" element={<ChatPage />} />
                    {/* Compatibility report/chat render inside ChatPage via the prefix check above */}
                    <Route
                      path="/compatibility-synastry/:requestId"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/compatibility-chat/:requestId"
                      element={<ChatPage />}
                    />

                    <Route path="/tarotCard" element={<Themed><TarotCardCategory /></Themed>} />
                    {/* <Route path="/uranian" element={<ChatPage />} /> */}
                    <Route path="/lifeGraph" element={<ChatPage />} />
                    <Route path="/lifeGps" element={<ChatPage />} />
                    <Route path="/subscription" element={<Themed><Subscription /></Themed>} />
                    <Route
                      path="/payment-success"
                      element={<Themed><PaymentSuccess /></Themed>}
                    />
                    <Route path="/payment-cancel" element={<Themed><PaymentCancel /></Themed>} />

                    {/* Legal */}
                    <Route path="/terms" element={<Themed><Terms /></Themed>} />
                    <Route path="/privacy" element={<Themed><Privacy /></Themed>} />
                    <Route
                      path="/refund-policy"
                      element={<Themed><RefundPolicy /></Themed>}
                    />
                    <Route path="/energyMatch" element={<ChatPage />} />
                    <Route path="/energyMatchV2" element={<ChatPage />} />
                    <Route path="/energyMatchV3" element={<ChatPage />} />
                    <Route
                      path="/energyMatchV3/result"
                      element={<ChatPage />}
                    />
                    <Route path="/marriage-verdict" element={<ChatPage />} />
                    <Route
                      path="/marriage-verdict/result"
                      element={<ChatPage />}
                    />
                    <Route path="/checkmate" element={<ChatPage />} />
                    <Route path="/checkmate/result" element={<ChatPage />} />
                    <Route path="/matescan" element={<ChatPage />} />
                    <Route path="/matescan/result" element={<ChatPage />} />
                    <Route path="/saju" element={<ChatPage />} />
                    <Route path="/saju/result" element={<ChatPage />} />
                    <Route path="/jp-tarot" element={<ChatPage />} />
                    <Route path="/astria-japan/kyusei" element={<ChatPage />} />
                    <Route
                      path="/astria-japan/kyusei/result"
                      element={<ChatPage />}
                    />
                    <Route path="/astria-japan/timing" element={<ChatPage />} />
                    <Route
                      path="/astria-japan/timing/result"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-japan/companion"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-japan/companion/result"
                      element={<ChatPage />}
                    />
                    <Route path="/astria-gcc-v2/dob" element={<ChatPage />} />
                    <Route
                      path="/astria-gcc-v2/timing"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-gcc-v2/kyusei"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-gcc-v2/companion"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-gcc-v2/emotional-intelligence"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-gcc-v2/personality-engine"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/sambandh"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/vivah"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/upay"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/bhavna"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/vyaktitva"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/samay"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-india-v2/aapka-note"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/energy-match"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/matescan"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/relationship"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/compatibility"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/companion-talk"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/cosmic-uk"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/daily-flow"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/emotional-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/zodiac-personality"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-uk-v2/zodiac-home"
                      element={<ChatPage />}
                    />
                    <Route path="/astria-mexico/home" element={<ChatPage />} />
                    <Route
                      path="/astria-mexico/energy-match"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/compatibility"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/cosmic-message"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/relationship"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/companion-talk"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/daily-flow"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico/zodiac-personality"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/soft-summary"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/connection-atmosphere"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/timing-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/emotion-balance"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/guidance-path"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-philippines-v2/your-note"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/soft-summary"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/connection-atmosphere"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/timing-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/emotion-balance"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/guidance-path"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-indonesia-v2/your-note"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/soft-summary"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/connection-atmosphere"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/timing-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/emotion-balance"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/guidance-path"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam-v2/your-note"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam/birth-chart"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam/lucky-day"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam/compatibility"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam/space-energy"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-vietnam/tarot"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/soft-summary"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/connection-atmosphere"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/timing-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/emotion-balance"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/guidance-path"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-brazil-v2/your-note"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/soft-summary"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/connection-atmosphere"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/timing-rhythm"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/emotion-balance"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/guidance-path"
                      element={<ChatPage />}
                    />
                    <Route
                      path="/astria-mexico-v2/your-note"
                      element={<ChatPage />}
                    />
                    <Route path="/compatibility-form" element={<ChatPage />} />
                    <Route path="/matches" element={<ChatPage />} />
                    <Route path="/history" element={<ChatPage />} />

                    {/* Admin */}
                    <Route
                      path="/admin"
                      element={
                        <ProtectedRoute>
                          <AdminPanel />
                        </ProtectedRoute>
                      }
                    >
                      <Route index element={<CategoryManager />} />
                      <Route
                        path="vocabulary"
                        element={<VocabularyManager />}
                      />
                      <Route path="users" element={<UserManager />} />
                      <Route
                        path="subscriptions"
                        element={<SubscriptionPlanManager />}
                      />
                      <Route path="settings" element={<Settings />} />
                      <Route path="indexing" element={<IndexBuilderPage />} />
                    </Route>

                    {/* Apple Auth */}
                    <Route
                      path="/auth/apple/callback"
                      element={<AppleCallback />}
                    />
                    <Route path="/apple/success" element={<AppleSuccess />} />

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </DomainGate>
              </ChatProvider>
            </SubCategoryProvider>
          </CategoryProvider>
        </HeadlineProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
